import { create } from 'zustand';
import type { AppSettings, DentalProgram, StudentProfile, ThemeMode } from '../types';
import { DEFAULT_SETTINGS } from '../types';
import { createProvider, providerContext, type AIProvider } from '../ai';
import type { ProviderContext } from '../ai/provider';
import { codexVersion } from '../ai/tauri';
import { isHostedWeb } from '../config/cloud';
import { initDatabase } from '../db/database';
import { seedDatabaseIfNeeded } from '../db/seed';
import { installGlobalErrorLogging } from '../utils/logging';
import * as repo from '../db/repository';
import { initAuth } from '../auth/auth';
import { startSync } from '../sync/controller';

export interface AiStatus {
  state: 'unknown' | 'checking' | 'ready' | 'unavailable';
  /** e.g. "codex-cli 0.139.0" or the failure reason. */
  detail: string;
}

interface AppState {
  ready: boolean;
  initError: string | null;
  settings: AppSettings;
  profile: StudentProfile | null;
  programs: DentalProgram[];
  provider: AIProvider;
  ctx: ProviderContext;
  aiStatus: AiStatus;
  toast: { message: string; severity: 'success' | 'error' | 'info' } | null;

  init: () => Promise<void>;
  refreshProfile: () => void;
  refreshPrograms: () => void;
  saveSettings: (settings: AppSettings) => void;
  setThemeMode: (mode: ThemeMode) => void;
  /** Cheap `codex --version` probe feeding the AI-status chip. */
  checkAiStatus: () => Promise<AiStatus>;
  completeOnboarding: () => void;
  reopenOnboarding: () => void;
  showToast: (message: string, severity?: 'success' | 'error' | 'info') => void;
  clearToast: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  ready: false,
  initError: null,
  settings: DEFAULT_SETTINGS,
  profile: null,
  programs: [],
  provider: createProvider(DEFAULT_SETTINGS),
  ctx: providerContext(DEFAULT_SETTINGS),
  aiStatus: { state: 'unknown', detail: '' },
  toast: null,

  init: async () => {
    try {
      installGlobalErrorLogging();
      await initDatabase();
      await seedDatabaseIfNeeded();
      const settings = repo.getSettings();
      set({
        ready: true,
        settings,
        profile: repo.getActiveProfile(),
        programs: repo.listPrograms(),
        provider: createProvider(settings),
        ctx: providerContext(settings),
      });
      // Non-blocking: the chip updates when the probe returns.
      void get().checkAiStatus();

      // Cloud auth + sync are fully optional and must never block first paint or
      // break the offline app: initialize MSAL and start the sync loops in the
      // background, swallowing any failure (e.g. no network, sync disabled).
      void (async () => {
        try {
          await initAuth();
        } catch {
          /* auth stays signed-out; the app is fully usable offline */
        }
        try {
          startSync();
        } catch {
          /* sync stays dormant until the user links an account */
        }
        try {
          const { initTelemetry } = await import('../telemetry/telemetry');
          initTelemetry();
        } catch {
          /* telemetry is best-effort */
        }
      })();
    } catch (e) {
      set({ initError: (e as Error).message });
    }
  },

  refreshProfile: () => set({ profile: repo.getActiveProfile() }),
  refreshPrograms: () => set({ programs: repo.listPrograms() }),

  saveSettings: (settings) => {
    repo.saveSettings(settings);
    set({
      settings,
      provider: createProvider(settings),
      ctx: providerContext(settings),
    });
  },

  setThemeMode: (mode) => {
    get().saveSettings({ ...get().settings, themeMode: mode });
  },

  checkAiStatus: async () => {
    // Hosted web: AI runs through the managed cloud service — there is nothing
    // to install or configure, so the status is always ready.
    if (isHostedWeb()) {
      const status: AiStatus = { state: 'ready', detail: 'Cloud AI' };
      set({ aiStatus: status });
      return status;
    }
    set({ aiStatus: { state: 'checking', detail: '' } });
    const res = await codexVersion(get().settings.codexPath);
    const status: AiStatus = res.ok
      ? { state: 'ready', detail: res.detail }
      : { state: 'unavailable', detail: res.detail };
    set({ aiStatus: status });
    return status;
  },

  completeOnboarding: () => {
    get().saveSettings({ ...get().settings, onboardedAt: new Date().toISOString() });
  },

  reopenOnboarding: () => {
    get().saveSettings({ ...get().settings, onboardedAt: null });
  },

  showToast: (message, severity = 'success') => set({ toast: { message, severity } }),
  clearToast: () => set({ toast: null }),
}));
